import React from 'react';
import { Settings, Schedule, Message, Apps, Computer, Business } from '@material-ui/icons';
import { AdminConsoleAdminsPanel } from '../admin-console-content-pages/admin-console-admins/AdminConsoleAdminsPanel';
import { AdminConsoleOnboardingApplicationsPanel } from '../admin-console-content-pages/admin-console-onboarding-applications/AdminConsoleOnboardingApplicationsPanel';
import { AdminConsoleBulletinSettings } from '../admin-console-content-pages/admin-console-bulletin-settings/AdminConsoleBulletinSettings';
import { AdminConsoleIncidentReportingPanel } from '../admin-console-content-pages/admin-console-incident-reporting/AdminConsoleIncidentReportingPanel';
import { AdminConsoleOperationsPanel } from '../admin-console-content-pages/admin-console-operations/AdminConsoleOperationsPanel';
import { AdminConsoleFallbackPage } from '../admin-console-content-pages/AdminConsoleFallbackPage';
import { AdminConsoleResourcesPanel } from '../admin-console-content-pages/admin-console-resources/AdminConsoleResourcesPanel';
import { AdminConsoleLabelsPanel } from '../admin-console-content-pages/admin-console-labels/AdminConsoleLabelsPanel';
import { AdminConsoleMatchingPanel } from '../admin-console-content-pages/admin-console-matching/AdminConsoleMatchingPanel';
import { AdminConsoleRoles } from '../admin-console-content-pages/admin-console-roles/AdminConsoleRoles';
import { AdminConsoleTags } from '../admin-console-content-pages/admin-console-tags/AdminConsoleTags';
import { AdminConsoleCommunicationPreferences } from '../Communications/Preferences/AdminConsoleCommunicationPreferences';
import { AdminConsoleCommunicationTemplate } from '../Communications/Templates/AdminConsoleCommunicationTemplate';
import { AdminConsoleCommunicationReminders } from '../Communications/Reminders/AdminConsoleCommunicationReminders';
import { AdminConsoleCommunicationNotifications } from '../Communications/Notifications/AdminConsoleCommunicationNotifications';
import { AdminConsoleChatSettings } from '../Communications/Chat/AdminConsoleChatSettings';
import { AdminConsoleEmbeddableWidgetsPanel } from '../admin-console-content-pages/Integrations/EmbeddableWidgets/AdminConsoleEmbeddableWidgetsPanel';
import { AdminConsoleAppsScreen } from '../admin-console-content-pages/Integrations/Apps/AdminConsoleAppsScreen';
import { useOrgInfo } from '../../../../global/auth/use-org-info/use-org-info';
import { HideablePageId } from '../../../../global/auth/use-hidden-pages/hideable-pages';
import { isPageHidden } from '../../../../global/auth/use-hidden-pages/use-hidden-pages';
import { AdminConsoleActivityReportingPanel } from '../admin-console-content-pages/admin-console-activity-reporting/AdminConsoleActivityReportingPanel';
import { AdminConsoleSecurityPanel } from '../admin-console-content-pages/admins-console-security/AdminConsoleSecurityPanel';
import { AdminConsoleBilling } from '../admin-console-content-pages/admin-console-billing/AdminConsoleBilling';
import { AdminConsoleFunnels } from '../admin-console-content-pages/admin-console-funnels/AdminConsoleFunnels';
import {
  AdminConsolePendingUpdates,
  AdminConsoleUpdateHistory,
} from '../admin-console-content-pages/admin-console-enterprise/EnterprisePanels';
import { AdminConsolePushHistory } from '../admin-console-content-pages/admin-console-enterprise/AdminConsolePushHistory';
import { AdminConsolePushUpdates } from '../admin-console-content-pages/admin-console-enterprise/AdminConsolePushUpdates';
import { AdminConsoleSubAccounts } from '../admin-console-content-pages/admin-console-enterprise/AdminConsoleSubAccounts';
import { AdminConsoleSubAccountDetail } from '../admin-console-content-pages/admin-console-enterprise/AdminConsoleSubAccountDetail';

interface Route {
  id?: HideablePageId;
  label: string;
  route: string;
  component: React.ElementType;
  enabled?: boolean;
}
interface RouteGroups {
  heading: string;
  icon: React.ElementType;
  routeOptions: Route[];
}

interface RoutesProps {
  routeGroups: RouteGroups[];
}

export const useAdminConsoleRoutesMap = (route: string): RoutesProps => {
  const orgInfo = useOrgInfo();

  const isEnterpriseMaster = orgInfo?.isEnterprise ?? false;
  const isEnterpriseSub = (orgInfo?.enterpriseParentTenantId ?? null) !== null;

  const enterpriseRouteOptions: Route[] = [
    {
      label: '',
      route: `${route}/sub-accounts/:subTenantId`,
      component: AdminConsoleSubAccountDetail,
      enabled: isEnterpriseMaster,
    },
    {
      label: 'Sub-Accounts',
      route: `${route}/sub-accounts`,
      component: AdminConsoleSubAccounts,
      enabled: isEnterpriseMaster,
    },
    {
      label: 'Push Updates',
      route: `${route}/push-updates`,
      component: AdminConsolePushUpdates,
      enabled: isEnterpriseMaster,
    },
    {
      label: 'Push History',
      route: `${route}/push-history`,
      component: AdminConsolePushHistory,
      enabled: isEnterpriseMaster,
    },
    {
      label: 'Pending Updates',
      route: `${route}/pending-updates`,
      component: AdminConsolePendingUpdates,
      enabled: isEnterpriseSub,
    },
    {
      label: 'Update History',
      route: `${route}/update-history`,
      component: AdminConsoleUpdateHistory,
      enabled: isEnterpriseSub,
    },
  ];

  const routesProps: RoutesProps = {
    routeGroups: [
      {
        heading: 'System',
        icon: Computer,
        routeOptions: [
          {
            label: 'Admins',
            id: 'admin-console.admins',
            route: `${route}/admins`,
            component: AdminConsoleAdminsPanel,
          },
          {
            label: 'Billing',
            id: 'admin-console.billing',
            route: `${route}/billing`,
            component: AdminConsoleBilling,
          },
          {
            label: 'Security',
            id: 'admin-console.security',
            route: `${route}/security`,
            component: AdminConsoleSecurityPanel,
          },
        ],
      },
      {
        heading: 'General',
        icon: Settings,
        routeOptions: [
          {
            label: 'Onboarding',
            id: 'admin-console.onboarding',
            route: `${route}/onboarding`,
            component: AdminConsoleOnboardingApplicationsPanel,
          },
          {
            label: 'Funnels',
            id: 'admin-console.funnels',
            route: `${route}/funnels`,
            component: AdminConsoleFunnels,
          },
          {
            label: 'Bulletins',
            id: 'admin-console.bulletins',
            route: `${route}/bulletins`,
            component: AdminConsoleBulletinSettings,
            enabled: orgInfo?.packageConfig ? orgInfo.packageConfig.bulletins : true,
          },
          {
            label: 'Resources',
            id: 'admin-console.resources',
            route: `${route}/resources`,
            component: AdminConsoleResourcesPanel,
          },
          {
            label: 'Application Actions',
            id: 'admin-console.matching',
            route: `${route}/matching`,
            component: AdminConsoleMatchingPanel,
            enabled: orgInfo?.packageConfig ? orgInfo.packageConfig.integrations : true,
          },
          {
            label: 'Custom Labels',
            id: 'admin-console.labels',
            route: `${route}/labels`,
            component: AdminConsoleLabelsPanel,
          },
        ],
      },
      {
        heading: 'Activities & Rosters',
        icon: Schedule,
        routeOptions: [
          {
            label: 'Operations',
            id: 'admin-console.operations',
            route: `${route}/operations`,
            component: AdminConsoleOperationsPanel,
            enabled: orgInfo?.packageConfig ? orgInfo.packageConfig.rostering : true,
          },
          {
            label: 'Activity Reporting',
            id: 'admin-console.reporting',
            route: `${route}/reporting`,
            component: AdminConsoleActivityReportingPanel,
            enabled: orgInfo?.packageConfig ? orgInfo.packageConfig.rostering : true,
          },
          {
            label: 'Tags',
            id: 'admin-console.tags',
            route: `${route}/tags`,
            component: AdminConsoleTags,
            enabled: orgInfo?.packageConfig ? orgInfo.packageConfig.rostering : true,
          },
          {
            label: 'Incident Reporting',
            id: 'admin-console.incident-reporting',
            route: `${route}/incident-reporting`,
            component: AdminConsoleIncidentReportingPanel,
            enabled: orgInfo?.packageConfig ? orgInfo.packageConfig.incidents : true,
          },
          {
            label: 'Roles',
            id: 'admin-console.roles',
            route: `${route}/roles`,
            component: AdminConsoleRoles,
          },
        ],
      },
      {
        heading: 'Communications',
        icon: Message,
        routeOptions: [
          {
            label: 'Preferences',
            id: 'admin-console.preferences',
            route: `${route}/preferences`,
            component: AdminConsoleCommunicationPreferences,
          },
          {
            label: 'Templates',
            id: 'admin-console.templates',
            route: `${route}/templates`,
            component: AdminConsoleCommunicationTemplate,
          },
          {
            label: 'Reminders & Automations',
            id: 'admin-console.reminders',
            route: `${route}/reminders`,
            component: AdminConsoleCommunicationReminders,
          },
          {
            label: 'Notifications & Alerts',
            id: 'admin-console.notifications',
            route: `${route}/notifications`,
            component: AdminConsoleCommunicationNotifications,
          },
          {
            label: 'Chat',
            id: 'admin-console.chat',
            route: `${route}/chat`,
            component: AdminConsoleChatSettings,
          },
        ],
      },
      {
        heading: 'Integrations',
        icon: Apps,
        routeOptions: [
          {
            label: 'Apps',
            id: 'admin-console.apps',
            route: `${route}/apps`,
            component: AdminConsoleAppsScreen,
            enabled: orgInfo?.packageConfig ? orgInfo.packageConfig.integrations : true,
          },
          {
            label: 'Embeddable Widgets',
            id: 'admin-console.embeddable-widgets',
            route: `${route}/embeddable-widgets`,
            component: AdminConsoleEmbeddableWidgetsPanel,
            enabled: orgInfo?.packageConfig ? orgInfo.packageConfig.integrations : true,
          },
        ],
      },
      {
        heading: 'Enterprise',
        icon: Business,
        routeOptions: enterpriseRouteOptions,
      },
      {
        heading: 'Fallback',
        icon: Settings,
        routeOptions: [
          {
            label: '',
            route: `${route}`,
            component: AdminConsoleFallbackPage,
          },
        ],
      },
    ],
  };

  const hiddenPages = orgInfo?.hiddenPages;

  if (!hiddenPages || hiddenPages.length === 0) {
    return routesProps;
  }

  return {
    routeGroups: routesProps.routeGroups.map((group) => ({
      ...group,
      routeOptions: group.routeOptions.map((option) =>
        isPageHidden(hiddenPages, option.id) ? { ...option, enabled: false } : option
      ),
    })),
  };
};

export const adminConsoleFallbackPath = 'admins';
